// Calendar view: a hand-built month grid (no calendar library). Each day is a
// density heat-cell whose brightness scales with how many events fall on it,
// with a row of category-colored bars showing the mix. A radius control (synced
// with the search bar) re-runs the search. Selecting a day reveals an inline
// agenda of that day's events below the grid.

import { categoryMeta } from '../lib/categories';
import { RADIUS_OPTIONS } from '../lib/constants';
import { formatTime, localDayKey } from '../lib/format';
import { navigate } from '../lib/router';
import { monthEvents, store, type AppState } from '../lib/state';
import type { NormalizedEvent } from '../types/event';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface CalendarHandlers {
  runSearch: () => Promise<void>;
}

function openEvent(e: NormalizedEvent): void {
  const url = e.sourceUrl;
  if (!url) return;
  if (e.source === 'local') {
    try {
      navigate(new URL(url).pathname);
    } catch {
      navigate(url);
    }
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

export function createCalendarView(root: HTMLElement, handlers: CalendarHandlers): void {
  // Local navigation state: the first day of the shown month.
  let shown = startOfMonth(new Date());
  let anchored = false; // snap to the first event month once results arrive

  function eventsByDay(state: AppState): Map<string, NormalizedEvent[]> {
    const map = new Map<string, NormalizedEvent[]>();
    for (const e of monthEvents(state)) {
      const key = localDayKey(e.start);
      const list = map.get(key) ?? map.set(key, []).get(key)!;
      list.push(e);
    }
    return map;
  }

  function render(state: AppState): void {
    root.innerHTML = '';

    if (state.status !== 'ready') {
      root.innerHTML = `<div class="placeholder">${
        state.status === 'loading' ? 'Loading the calendar...' : 'Search to see the calendar.'
      }</div>`;
      return;
    }

    const byDay = eventsByDay(state);

    // On first render after a search, jump to the earliest event's month.
    if (!anchored && byDay.size > 0) {
      const firstKey = [...byDay.keys()].sort()[0];
      const [y, m] = firstKey.split('-').map(Number);
      shown = new Date(y, m - 1, 1);
      anchored = true;
    }

    // --- Header: month navigation ---
    const header = document.createElement('div');
    header.className = 'cal-header';
    const prev = navButton('‹', 'Previous month', () => {
      shown = addMonths(shown, -1);
      render(store.get());
    });
    const next = navButton('›', 'Next month', () => {
      shown = addMonths(shown, 1);
      render(store.get());
    });
    const title = document.createElement('h3');
    title.className = 'cal-title';
    title.textContent = `${MONTHS[shown.getMonth()]} ${shown.getFullYear()}`;
    header.append(prev, title, next);
    root.appendChild(header);

    // --- Toolbar: radius control + heat legend ---
    const toolbar = document.createElement('div');
    toolbar.className = 'cal-toolbar';

    const radiusWrap = document.createElement('label');
    radiusWrap.className = 'cal-radius';
    radiusWrap.innerHTML = `<span>Within</span>`;
    const radius = document.createElement('select');
    radius.setAttribute('aria-label', 'Search radius in miles');
    for (const r of RADIUS_OPTIONS) {
      const opt = document.createElement('option');
      opt.value = String(r);
      opt.textContent = `${r} mi`;
      if (r === state.radiusMiles) opt.selected = true;
      radius.appendChild(opt);
    }
    radius.addEventListener('change', () => {
      store.set({ radiusMiles: Number(radius.value) });
      if (store.get().location) void handlers.runSearch();
    });
    radiusWrap.appendChild(radius);

    const legend = document.createElement('div');
    legend.className = 'cal-legend';
    legend.innerHTML = `
      <span>Fewer</span>
      <i class="cal-legend-swatch" style="--heat:0.15"></i>
      <i class="cal-legend-swatch" style="--heat:0.45"></i>
      <i class="cal-legend-swatch" style="--heat:0.75"></i>
      <i class="cal-legend-swatch" style="--heat:1"></i>
      <span>More</span>`;

    toolbar.append(radiusWrap, legend);
    root.appendChild(toolbar);

    // --- Grid ---
    const grid = document.createElement('div');
    grid.className = 'cal-grid';
    for (const w of WEEKDAYS) {
      const cell = document.createElement('div');
      cell.className = 'cal-weekday';
      cell.textContent = w;
      grid.appendChild(cell);
    }

    const firstWeekday = shown.getDay();
    const daysInMonth = new Date(shown.getFullYear(), shown.getMonth() + 1, 0).getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Normalize heat against the busiest day in this month.
    let maxCount = 1;
    for (let d = 1; d <= daysInMonth; d++) {
      const k = localDayKey(new Date(shown.getFullYear(), shown.getMonth(), d));
      maxCount = Math.max(maxCount, byDay.get(k)?.length ?? 0);
    }

    for (let i = 0; i < firstWeekday; i++) {
      const blank = document.createElement('div');
      blank.className = 'cal-day blank';
      grid.appendChild(blank);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(shown.getFullYear(), shown.getMonth(), d);
      const key = localDayKey(date);
      const dayEvents = byDay.get(key) ?? [];
      const count = dayEvents.length;
      const isPast = date < today;

      const cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'cal-day';
      if (isPast) cell.classList.add('past');
      if (count > 0 && !isPast) cell.classList.add('has-events');
      if (key === localDayKey(today)) cell.classList.add('today');
      if (state.selectedDay === key) cell.classList.add('selected');
      cell.disabled = isPast || count === 0;
      cell.style.setProperty('--heat', String(count > 0 && !isPast ? count / maxCount : 0));
      cell.setAttribute(
        'aria-label',
        `${MONTHS[date.getMonth()]} ${d}` + (count ? `, ${count} event${count === 1 ? '' : 's'}` : '')
      );

      const num = document.createElement('span');
      num.className = 'cal-num';
      num.textContent = String(d);
      cell.appendChild(num);

      if (count > 0 && !isPast) {
        // Category-colored bars: up to 5, conveying both magnitude and the mix
        // of event types that day.
        const bars = document.createElement('span');
        bars.className = 'cal-bars';
        for (const e of dayEvents.slice(0, 5)) {
          const bar = document.createElement('i');
          bar.style.background = categoryMeta(e.category).color;
          bars.appendChild(bar);
        }
        if (count > 5) bars.classList.add('more');
        cell.appendChild(bars);
      }

      cell.addEventListener('click', () => store.selectDay(key));
      grid.appendChild(cell);
    }

    root.appendChild(grid);

    // --- Inline agenda for the selected day ---
    if (state.selectedDay) {
      root.appendChild(buildAgenda(state.selectedDay, byDay.get(state.selectedDay) ?? []));
    } else {
      const hint = document.createElement('p');
      hint.className = 'cal-hint';
      hint.textContent = 'Pick a day to see what is happening.';
      root.appendChild(hint);
    }
  }

  function buildAgenda(dayKey: string, events: NormalizedEvent[]): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'cal-agenda';

    const head = document.createElement('div');
    head.className = 'cal-agenda-head';
    const [y, m, d] = dayKey.split('-').map(Number);
    const label = new Date(y, m - 1, d).toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
    const h4 = document.createElement('h4');
    h4.textContent = label;
    head.appendChild(h4);
    if (events.length > 0) {
      const cards = navButton('View as cards', 'View this day as cards', () => store.setView('list'));
      cards.className = 'btn ghost small';
      head.appendChild(cards);
    }
    panel.appendChild(head);

    if (events.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'cal-hint';
      empty.textContent = 'No events this day.';
      panel.appendChild(empty);
      return panel;
    }

    const sorted = [...events].sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
    for (const e of sorted) {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'agenda-row';
      const meta = categoryMeta(e.category);
      const time = formatTime(e.start);
      row.innerHTML = `
        <span class="agenda-dot" style="background:${meta.color}"></span>
        <span class="agenda-time">${time || 'All day'}</span>
        <span class="agenda-main">
          <span class="agenda-title"></span>
          <span class="agenda-sub"></span>
        </span>`;
      row.querySelector<HTMLElement>('.agenda-title')!.textContent = e.title;
      row.querySelector<HTMLElement>('.agenda-sub')!.textContent =
        [e.venueName, e.city].filter(Boolean).join(', ') || meta.label;
      row.addEventListener('click', () => openEvent(e));
      panel.appendChild(row);
    }
    return panel;
  }

  // Reset the month anchor whenever a brand new search lands.
  let lastGeneratedAt = '';
  store.subscribe((state) => {
    if (state.meta?.generatedAt && state.meta.generatedAt !== lastGeneratedAt) {
      lastGeneratedAt = state.meta.generatedAt;
      anchored = false;
    }
    render(state);
  });
  render(store.get());
}

function navButton(label: string, aria: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'cal-nav';
  b.textContent = label;
  b.setAttribute('aria-label', aria);
  b.addEventListener('click', onClick);
  return b;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
